// apps/backoffice/src/app/(public)/public/activities/Content.tsx
'use client';

import Script from 'next/script';
import { preconnect } from 'react-dom';
import type { AppLocale } from '@tourtask/i18n/config';
import { TOURTASK_API_ORIGIN, TOURTASK_MEDIA_ORIGIN } from '@tourtask/utils/shared/net/tourtaskOrigins';
import type { HostedShopPage } from './hostedShopSeo';

interface ContentProps {
  /** The widget's page (`data-page`): the tours or the rentals. */
  page: HostedShopPage;
  businessId: number;
  businessToken: string;
  locale: AppLocale;
  appRootUrl: string;
  /** The catalog view's document title (`hostedShopSeo.resolveHostedShopCatalogHead`). */
  catalogTitle: string;
  /** The catalog view's meta description; `null` when it has none. */
  catalogDescription: string | null;
  /**
   * The page colour of the business's brand (`#rrggbb`, its scheme's page; docs/brand-colours.md §4.3). The shop
   * paints no background of its own (E10, decision 7), so this page, the shop's frame here, paints it.
   */
  pageBackground: string;
}

export function Content({
  page,
  businessId,
  businessToken,
  locale,
  appRootUrl,
  catalogTitle,
  catalogDescription,
  pageBackground,
}: ContentProps) {
  // The widgets' API connection (anonymous, as their calls are) and the shop's photo connection, set up while the
  // scripts download (docs/embed-size-and-speed.md §6.1 #2). React hoists both into the server's <head>. The scripts
  // themselves need no preload here: next/script already preloads an `afterInteractive` src in the App Router.
  preconnect(TOURTASK_API_ORIGIN, { crossOrigin: 'anonymous' });
  preconnect(TOURTASK_MEDIA_ORIGIN);
  return (
    <div style={{ backgroundColor: pageBackground, minHeight: '100vh' }}>
      {/* CSS */}
      <link
        rel="stylesheet"
        href="https://tourtask-booking.pages.dev/static/css/main.css"
      />
      <div
        id="tourtask-booking-root"
        data-page={page}
        data-business-id={businessId}
        data-business-token={businessToken}
        data-lang={locale}
        data-app-root-url={appRootUrl}
        // This page is served with a TOUR's head on `?tt-tour=`; these tell the
        // widget what to restore when the visitor closes that tour
        // (docs/embed-seo.md §3). An empty description is the attribute's way of
        // saying the catalog view has none.
        data-catalog-title={catalogTitle}
        data-catalog-description={catalogDescription ?? ''}
      />

      {/* JS – must load AFTER the div exists */}
      <Script
        src="https://tourtask-booking.pages.dev/static/js/main.js"
        strategy="afterInteractive"
      />

      {/*
        Site-wide chat launcher on the TourTask-hosted shop. Operators without
        their own website book through this page, so it gets the same chat widget
        they'd get from the copy-paste snippet on their own site. The widget
        self-gates: its bootstrap (resolveWidgetMode) is the single source of
        truth for "is chat enabled", so we mount it unconditionally and it
        renders live chat / leave-a-message / the frozen notice when enabled and
        nothing when the business has chat fully off — exactly as on a partner
        site. IMMUTABLE asset URLs (apps/embed/AGENTS.md): the chat bundle is the
        embed's second craco entry, pinned at static/js|css/chat.* on the
        tourtask-booking Cloudflare Pages slug — never rename without a
        coordinated migration + plugin release.
      */}
      <link
        rel="stylesheet"
        href="https://tourtask-booking.pages.dev/static/css/chat.css"
      />
      <div
        id="tourtask-chat-root"
        data-business-id={businessId}
        data-business-token={businessToken}
        data-lang={locale}
      />
      <Script
        src="https://tourtask-booking.pages.dev/static/js/chat.js"
        strategy="afterInteractive"
      />
    </div>
  );
}